import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Button, LinkButton } from '@/components/ui/Button';

/**
 * Custom error UI for React Router's `errorElement`. Replaces the default
 * "Unexpected Application Error — hey developer…" page with something
 * end-user-appropriate, while still surfacing the underlying error message
 * in a collapsible detail block for debugging.
 */
export function ErrorPage() {
  const error = useRouteError();
  const { title, body, status } = describeError(error);

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <img
            src="/Asset%2042.png"
            alt="Labyrinth Studios"
            style={{ height: 100, width: 'auto', objectFit: 'contain' }}
          />
        </div>

        {status != null && (
          <div style={statusPill}>{status}</div>
        )}

        <h1 style={h1}>{title}</h1>
        <p style={subhead}>{body}</p>

        <div style={btnRow}>
          <Button size="lg" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
          <LinkButton size="lg" variant="ghost" to="/dashboard">
            Back to Dashboard
          </LinkButton>
        </div>

        <details style={detailsBlock}>
          <summary style={summaryStyle}>Technical details</summary>
          <pre style={preStyle}>{formatErrorDetail(error)}</pre>
        </details>

        <p style={footer}>
          If this keeps happening, mention what you were doing right before it broke and
          send a screenshot to <strong>Oscar</strong>.
        </p>
      </div>
    </div>
  );
}

function describeError(error: unknown): { title: string; body: string; status: number | null } {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        status: 404,
        title: "We can't find that page",
        body: "The link you followed might be old, mistyped, or the page might have moved.",
      };
    }
    if (error.status === 403) {
      return {
        status: 403,
        title: "You don't have access to that",
        body: "Your account role doesn't allow this. If you need access, ask an admin to update your role.",
      };
    }
    if (error.status === 401) {
      return {
        status: 401,
        title: 'Please sign in again',
        body: 'Your session has expired. Sign in with Microsoft to continue.',
      };
    }
    return {
      status: error.status,
      title: `Server returned ${error.status}`,
      body: error.statusText || 'Something went wrong on our end.',
    };
  }
  if (error instanceof Error) {
    return {
      status: null,
      title: 'Something went wrong',
      body: 'The page hit an unexpected problem. Try reloading — if it keeps happening, the technical details below can help us track it down.',
    };
  }
  return {
    status: null,
    title: 'Something went wrong',
    body: 'The page hit an unexpected problem. Try reloading the page.',
  };
}

function formatErrorDetail(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}\n\n${JSON.stringify(error.data, null, 2)}`;
  }
  if (error instanceof Error) {
    return `${error.name}: ${error.message}\n\n${error.stack ?? '(no stack trace)'}`;
  }
  return String(error);
}

/* ─── Styles ──────────────────────────────────────────────────────────── */

const page: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f0f0f5',
  padding: 20,
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: '40px 36px',
  width: 'min(540px, 100%)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  textAlign: 'center',
};

const statusPill: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  padding: '3px 10px',
  borderRadius: 10,
  background: '#fef3c7',
  color: '#92400e',
  marginBottom: 12,
};

const h1: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: '#1a1a2e',
  marginBottom: 10,
};

const subhead: React.CSSProperties = {
  fontSize: 14,
  color: '#666',
  lineHeight: 1.6,
  marginBottom: 24,
};

const btnRow: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'center',
  marginBottom: 24,
  flexWrap: 'wrap',
};

const detailsBlock: React.CSSProperties = {
  marginTop: 8,
  marginBottom: 16,
  textAlign: 'left',
  background: '#f8f9fa',
  borderRadius: 6,
  padding: 10,
};

const summaryStyle: React.CSSProperties = {
  cursor: 'pointer',
  fontSize: 12,
  color: '#666',
  fontWeight: 600,
};

const preStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#555',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: '10px 0 0',
  maxHeight: 200,
  overflowY: 'auto',
};

const footer: React.CSSProperties = {
  fontSize: 11,
  color: '#999',
  marginTop: 12,
};
